import { Check, Moon, Sun } from 'lucide-react';

import { useTheme, type Theme } from '@/components/theme-provider';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const OPTIONS: Array<{ value: Theme; label: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

interface ModeToggleProps {
  /**
   * `compact` matches the size-8 controls in the shell's h-14 header strip.
   * Default keeps shadcn's standard icon-button size for use elsewhere.
   */
  compact?: boolean;
}

/**
 * Light/dark/system switcher, mounted in the shell header beside the bell.
 *
 * Held back until the page conversion batches landed — while screens were
 * hard-coded to light, exposing this would have shipped a visibly broken dark
 * mode. The colour literals are now down to a documented handful (see
 * lib/styleBudget.test.ts).
 */
export function ModeToggle({ compact = false }: ModeToggleProps) {
  const { theme, setTheme } = useTheme();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Change theme"
          className={cn(compact && 'size-8 text-muted-foreground hover:text-foreground')}
        >
          <Sun className="scale-100 rotate-0 transition-all dark:scale-0 dark:-rotate-90" />
          <Moon className="absolute scale-0 rotate-90 transition-all dark:scale-100 dark:rotate-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuGroup>
          {OPTIONS.map((opt) => (
            <DropdownMenuItem key={opt.value} onClick={() => setTheme(opt.value)}>
              {/* Ticks the stored preference, not resolvedTheme — otherwise
                  "System" would never appear selected, it would just light up
                  whichever of Light/Dark the OS currently resolves to. The
                  spacer keeps the labels aligned on unselected rows. */}
              {theme === opt.value
                ? <Check className="size-3.5" aria-hidden />
                : <span className="size-3.5" aria-hidden />}
              {opt.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
